<template>
    <div class="relative flex text-xs text-slate-700 text-center flex-col justify-start items-stretch rounded-lg p-0 w-full bg-black text-white overflow-hidden shadow-sm"
        :key="`thumb-${ratioKey}`"
        :class="{'border-indigo-400 text-indigo-700': props.isActive}"
        :style="containerStyle">
        <BackgroundLayer :src="thumbSrc" :adjust="thumbAdjust" />
        <template v-if="hasLayers">
            <template v-for="layer in visibleLayers" :key="layer.id">
                <div
                    v-if="layer.type === 'image' && layer.src"
                    class="absolute overflow-hidden"
                    :style="{ left: `${boxOf(layer).x}%`, top: `${boxOf(layer).y}%`, width: `${boxOf(layer).w}%` }"
                >
                    <img :src="layer.src" class="w-full h-auto block" :style="imageLayerStyle(layer)" alt="" draggable="false" />
                </div>
                <div
                    v-else-if="layer.type === 'video'"
                    class="absolute overflow-hidden rounded"
                    :style="{ left: `${boxOf(layer).x}%`, top: `${boxOf(layer).y}%`, width: `${boxOf(layer).w}%`, ...imageLayerStyle(layer) }"
                >
                    <img v-if="layer.videoId" :src="videoThumb(layer.videoId)" class="w-full h-auto block" alt="" draggable="false" />
                    <div v-else class="aspect-video w-full bg-slate-800 border border-dashed border-slate-600 rounded flex items-center justify-center text-[3cqi] text-slate-400">
                        ▶
                    </div>
                </div>
                <div
                    v-else-if="layer.type !== 'image'"
                    class="absolute thumb-layer"
                    :style="{
                        left: `${boxOf(layer).x}%`,
                        top: `${boxOf(layer).y}%`,
                        width: `${boxOf(layer).w}%`,
                        color: layerTextStyle(layer).color,
                        fontFamily: layerTextStyle(layer).fontFamily,
                        fontSize: layerTextStyle(layer).fontSize,
                        textAlign: layerTextStyle(layer).textAlign,
                    }"
                    v-html="layer.html || ''"
                ></div>
            </template>
        </template>
        <template v-else>
            <div class="relative flex w-full justify-center text-[4.2cqi] text-[#60A5FA] pt-1.5 px-1 font-medium"
                v-html="flat.title || flat.header"></div>
            <div class="thumb-layer relative flex flex-col justify-center items-center flex-1 w-full min-h-0 overflow-hidden text-[8cqi] text-white px-2 font-normal"
                :style="pageContentStyle"
                v-html="flat.content"></div>
            <div class="relative flex-none text-[4.2cqi] text-[#FDE68A] pb-1 px-1 font-medium"
                v-html="flat.subtitle || flat.footer"></div>
        </template>
    </div>
</template>
<script setup>
import { defineProps, computed } from 'vue';
import { getThumbnailUrl, getVideoId } from '@/utils/youtube';
import BackgroundLayer from './BackgroundLayer.vue';
import { flattenLayers, layerBox, imageLayerStyle } from '@/utils/presentationLayers';

const props = defineProps({
    page: { type: Object, required: true },
    isActive: { type: Number, required: true },
    songBackground: { type: String, default: null },
    songBackgroundAdjust: { type: Object, default: null },
    screen: { type: String, default: '4:3' },
});

const ratioKey = computed(() => {
    const raw = String(props.screen || '4:3');
    const [w, h] = raw.includes('/') ? raw.split('/').map(Number) : raw.split(':').map(Number);
    if (!(w > 0 && h > 0)) return '4-3';
    return `${w}-${h}`;
});

const aspectRatioCss = computed(() => {
    const raw = String(props.screen || '4:3');
    const [w, h] = raw.includes('/') ? raw.split('/').map(Number) : raw.split(':').map(Number);
    if (w > 0 && h > 0) return `${w} / ${h}`;
    return '4 / 3';
});

const containerStyle = computed(() => ({
    aspectRatio: aspectRatioCss.value,
    containerType: 'inline-size',
}));

const layers = computed(() => {
    const ls = props.page?.layers;
    return Array.isArray(ls) ? ls.filter((l) => l && typeof l === 'object') : [];
});

const hasLayers = computed(() => layers.value.length > 0);

const visibleLayers = computed(() => layers.value.filter((l) => l.visible !== false));

const boxOf = (layer) => layerBox(layer);

const layerTextStyle = (layer) => {
    const role = layer?.role;
    let defaultColor = '#FFFFFF';
    let defaultFontSize = '8cqi';
    if (role === 'header' || role === 'title') {
        defaultColor = '#60A5FA';
        defaultFontSize = '4.2cqi';
    } else if (role === 'footer' || role === 'subtitle') {
        defaultColor = '#FDE68A';
        defaultFontSize = '4.2cqi';
    }
    const ls = (layer && layer.style && typeof layer.style === 'object') ? layer.style : {};
    let fontSize = defaultFontSize;
    if (ls.fontSize) {
        const px = parseFloat(ls.fontSize);
        if (Number.isFinite(px) && px > 0) {
            // No palco principal (base ~800px), px escala para (px / 800) * 100 cqi no thumbnail
            fontSize = `${(px / 800) * 100}cqi`;
        }
    }
    return {
        fontSize,
        color: ls.color || defaultColor,
        fontFamily: ls.fontFamily || undefined,
        textAlign: ls.textAlign || 'center',
        ...imageLayerStyle(layer),
    };
};

const flat = computed(() => {
    const ls = props.page?.layers;
    if (Array.isArray(ls) && ls.length > 0) {
        return flattenLayers(ls);
    }
    return {
        title: props.page?.title || '',
        header: props.page?.header || '',
        content: props.page?.content || '',
        subtitle: props.page?.subtitle || '',
        footer: props.page?.footer || '',
        background: props.page?.background || '',
    };
});

const pageContentStyle = computed(() => {
    const color = props.page?.style?.color;
    return color ? { color } : {};
});

const thumbAdjust = computed(() => {
    const adjust = props.page?.backgroundAdjust ?? props.songBackgroundAdjust ?? null;
    if (!adjust) return null;
    return { ...adjust, mode: 'cover' };
});

const videoThumb = (videoId) => {
    if (!videoId) return '';
    try {
        return getThumbnailUrl(videoId, 'maxresdefault');
    } catch {
        return '';
    }
};

const thumbSrc = computed(() => {
    const raw = props.page?.videoId || props.songBackground;
    if (raw) {
        const vid = getVideoId(raw) || (raw.length === 11 ? raw : null);
        if (vid) {
            try {
                return getThumbnailUrl(vid, 'maxresdefault');
            } catch {
                return `https://i.ytimg.com/vi/${vid}/hqdefault.jpg`;
            }
        }
    }
    return '';
});
</script>

<style scoped>
.thumb-layer {
    line-height: 1.25;
    word-break: break-word;
    overflow-wrap: break-word;
}
.thumb-layer :is(p, span, div, li, h1, h2, h3, h4, h5, h6, strong, em, u, s, b, i, a) {
    font-size: inherit !important;
    color: inherit;
    line-height: inherit !important;
    text-align: inherit;
}
</style>
